import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

import { QUERY_WIDTHS } from "@utils/index";
import { BusinessModel } from "@interfaces/index";
import { useNotify } from "@hooks/index";
import { useCommonStore, useUserStore } from "@stores/index";
import {
  useUserGetProfile,
  useUserUpdateDefaultBusiness,
} from "@providers/index";
import {
  MobileHeader,
  ConfirmModal,
  DefaultBusinessCard,
  RegularBusinessCard,
  RequestCallCard,
} from "@components/index";
import { NewQueryDrawer } from "@pages/index";

export const MyBusinessesPage = () => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const setUserState = useUserStore(({ setState }) => setState);

  const [businesses, setBusinesses] = useState<BusinessModel[]>([]);
  const [defaultId, setDefaultId] = useState<string>("");

  const [newDefaultId, setNewDefaultId] = useState<string>("");

  const [showNewQueryDrawer, setShowNewQueryDrawer] = useState(false);

  const {
    isPending: isPendingGetProfile,
    isSuccess,
    data,
    isError,
    error,
  } = useUserGetProfile();

  useEffect(() => {
    if (isSuccess && data) {
      setBusinesses(data.businesses);
      setDefaultId(data.defaultBusiness.uniqueId);
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (isError && error) {
      notify("error", error.message);
    }
  }, [isError, error, notify]);

  const {
    mutate: mutateUserUpdateDefaultBusiness,
    isPending: isPendingUpdateDefaultBusiness,
  } = useUserUpdateDefaultBusiness(
    () => {
      setDefaultId(newDefaultId);
      setNewDefaultId("");
      const newDefaultBusiness = businesses.find(
        ({ uniqueId }) => uniqueId === newDefaultId,
      );
      setUserState({
        business: newDefaultBusiness,
      });
      notify(
        "success",
        `You have marked the business "${newDefaultBusiness!.name}" as default.`,
      );
    },
    ({ message }) => notify("error", message),
  );

  useEffect(() => {
    setState({
      isLoading: isPendingGetProfile || isPendingUpdateDefaultBusiness,
    });
  }, [isPendingGetProfile, isPendingUpdateDefaultBusiness, setState]);

  const handleIgnoreMileage = (uniqueId: string) => {
    console.log("Ignore mileage, uniqueId =", uniqueId);
  };

  return (
    <>
      {isMobile && <MobileHeader href="/profile" title="My Businesses" />}
      {!isMobile && (
        <div className="max-w-[552px] px-14 pt-12 text-400 font-bold text-black-100">
          My Businesses
        </div>
      )}
      <div className="flex flex-wrap gap-4 px-4 pt-10 md:px-14 md:pt-14">
        {businesses.map(({ uniqueId, name }) =>
          defaultId === uniqueId ? (
            <DefaultBusinessCard
              key={uniqueId}
              uniqueId={uniqueId}
              name={name}
              onIgnoreMileage={() => handleIgnoreMileage(uniqueId)}
            />
          ) : (
            <RegularBusinessCard
              key={uniqueId}
              uniqueId={uniqueId}
              name={name}
              onSetDefault={() => setNewDefaultId(uniqueId)}
              onIgnoreMileage={handleIgnoreMileage}
            />
          ),
        )}
        <RequestCallCard onRequestCall={() => setShowNewQueryDrawer(true)} />
      </div>
      <ConfirmModal
        show={!!newDefaultId}
        confirmButtonTitle="Yes"
        text1="Are you sure?"
        text2="You really want to mark as default this business?"
        onClickCancel={() => setNewDefaultId("")}
        onClickConfirm={() => mutateUserUpdateDefaultBusiness(newDefaultId)}
      />
      <NewQueryDrawer
        show={showNewQueryDrawer}
        onClose={() => setShowNewQueryDrawer(false)}
        title="Send Query"
        presetCategory="account"
        presetTopicId="13"
        presetCallRequested={true}
      />
    </>
  );
};
